import React, { ComponentType } from "react"
import { compose } from "@reduxjs/toolkit"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { FormikHelpers as FormikActions } from "formik"
import { Box, Grid, DialogContent, Typography, styled } from "@mui/material"

import i18n from "config/i18n"
import { useAppDispatch } from "redux/hooks"
import { withModalDebt, withClientManager } from "hocs/features"
import { getTimeslots } from "helpers/issue"
import { mobilePhoneCountryOptions } from "helpers/countries"
import PollingService from "redux/api/pollingService"
import { showAlert, showNotice } from "redux/features/notice/noticeSlice"
import { closeDialog } from "redux/features/dialog/dialogSlice"
import { makeIssue } from "redux/features/issue/issueThunk"
import Close from "components/ui/ModalClose"
import Title from "components/Issue/Title"
import Manager from "components/Issue/Manager"
import Form from "components/Issue/Modal/Create/Form"
import { IssueFormValues, ClientManagerType, DebtType } from "types"

type Props = {
  debt: DebtType
  manager: ClientManagerType
}

const IssueCreateComponent = ({ debt, manager }: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { reference_number } = debt

  const timeslotOptions = getTimeslots()
  const phoneCodeOptions = mobilePhoneCountryOptions()

  const handleClose = () => {
    if (PollingService.isPolling()) {
      return dispatch(
        showAlert({
          message: i18n.t("common.request_in_progress"),
        }),
      )
    }
    dispatch(closeDialog())
    navigate("/")
  }

  const handleSubmit = (
    values: IssueFormValues,
    actions: FormikActions<object>,
  ) => {
    const { phone_code, phone_number, date, timeslot } = values
    const phone = phone_code + phone_number

    dispatch(
      makeIssue({
        debt_id: debt.id,
        phone,
        date: date.toLocaleDateString(),
        timeslot,
      }),
    )
      .then((result) => {
        dispatch(showNotice({ message: result }))
        handleClose()
      })
      .catch((error) => {
        dispatch(showAlert({ message: error }))
      })
      .finally(() => {
        actions.setSubmitting(false)
      })
  }

  return (
    <DialogContent>
      <Close onClick={handleClose} />
      <Container
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Headline>
          <Title reference_number={reference_number}>
            {t("features.issue.title")}
          </Title>
          <Description variant="h6">
            {t("features.issue.description")}
          </Description>
          <Manager manager={manager} />
          <Description variant="h6">{t("features.issue.callback")}</Description>
        </Headline>
        <Content>
          <Form
            initialValues={{
              phone_code: phoneCodeOptions[0].value,
              phone_number: "",
              date: new Date(),
              timeslot: timeslotOptions[0].value,
            }}
            timeslotOptions={timeslotOptions}
            phoneCodeOptions={phoneCodeOptions}
            onSubmit={handleSubmit}
          />
        </Content>
      </Container>
    </DialogContent>
  )
}

export default compose<ComponentType>(
  withModalDebt,
  withClientManager,
)(IssueCreateComponent)

export const Container = styled(Grid)(() => ({
  margin: "0",
}))

export const Headline = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
}))

const Description = styled(Typography)(() => ({
  margin: "7px 0",
}))

const Content = styled(Box)(() => ({
  margin: "0 auto",
  maxWidth: "464px",
  padding: "0",
}))
